<template>
	<div class="basket-list-item mb-2">
		<slot name="before-basket-item"></slot>

		<div
			class="basket-item component-loading with-icon d-flex"
			:class="{
				'sending is-loading': waiting,
				'is-loading': isCheckoutReadonly
			}"
		>
			<div class="image-container">
				<a :href="basketItem.variation.data | itemURL">
					<lazy-img
						v-if="image"
						:image-url="image"
						:alt="altText"
						:title="itemName"
						:height="height"
                        :width="width"
						picture-class="d-block mw-100 mh-100"
						data-testing="basket-item-img"
					></lazy-img>
				</a>
			</div>

			<div class="meta-container-wrapper">
				<div class="meta-container-wrapper-inner">
					<div class="meta-container">
						<div class="position-relative w-100">
							<a
								:href="basketItem.variation.data | itemURL"
								class="item-name text-primary text-appearance text-break"
								data-testing="basket-item-name"
							>
								{{ basketItem.variation.data | itemName }}
							</a>

							<div class="item-description">
								<template v-if="isDataFieldVisible('basket.item.availability')">
									<div
										v-if="
											basketItem.variation.data.variation.availability &&
											basketItem.variation.data.variation.availability.names.name
										"
									>
										<span
											class="availability"
											:class="
												'availability_' + basketItem.variation.data.variation.availability.id
											"
											>{{
												basketItem.variation.data.variation.availability.id === 1 &&
												$translate("Eros::Template.availabilityAlternative") !== ""
													? $translate("Eros::Template.availabilityAlternative")
													: basketItem.variation.data.variation.availability.names.name
											}}</span
										>
									</div>
								</template>

								<div v-for="attribute in basketItem.variation.data.attributes">
									<strong>{{ attribute.attribute.names.name }}: </strong>
									<span>{{ attribute.value.names.name }}</span>
								</div>

								<template v-if="isDataFieldVisible('basket.item.customNumber')">
									<div v-if="basketItem.variation.data.variation.number">
										<strong>{{ $translate("Ceres::Template.basketItemNumber") }}:</strong>
										<span>{{ basketItem.variation.data.variation.number }}</span>
									</div>
								</template>
							</div>
						</div>
					</div>

					<div class="basket-item-container-right">
						<div class="price-box text-right">
							<div class="item-total-price font-weight-bold text-nowrap">
								{{
									(basketItem.quantity * unitPrice)
										| currency(basketItem.variation.data.prices.default.currency)
								}}
							</div>
						</div>

						<div class="qty-box-container">
							<quantity-input
								@quantity-change="updateQuantity"
								:value="basketItem.quantity"
								:waiting="isInputLocked || isCheckoutReadonly"
								:min="basketItem.variation.data.variation.minimumOrderQuantity"
								:max="basketItem.variation.data.variation.maximumOrderQuantity"
								:interval="basketItem.variation.data.variation.intervalOrderQuantity"
							>
							</quantity-input>
						</div>

						<div class="item-remove-container text-right mt-auto">
							<button
								class="btn btn-sm text-muted p-0"
								:class="{
									disabled: waiting || isBasketLoading || isCheckoutReadonly || waitingForDelete
								}"
								@click="deleteItem"
                                data-testing="basket-item-delete"
							>
								{{ $translate("Ceres::Template.basketDelete") }}
								<icon icon="trash-alt	" class="default-float" :loading="waitingForDelete"></icon>
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<slot name="after-basket-item"></slot>
	</div>
</template>

<script>
import ExceptionMap from "../../../../ceres/exceptions/ExceptionMap";
import TranslationService from "../../../../ceres/services/TranslationService";
import { isDefined, isNullOrUndefined } from "../../../../ceres/helper/utils";
import { mapState } from "vuex";

const NotificationService = require("../../../../ceres/services/NotificationService");

import BasketSetComponentList from "../../../../ceres/components/basket/list/BasketSetComponentList.vue";

export default {
    name: "basket-list-item",

	components: {
		BasketSetComponentList
	},
	props: {
		template: {
			type: String,
			default: "#vue-basket-list-item"
		},
		basketItem: Object,
		basketDetailsData: {
			type: Array,
			default: () => []
		},
		isPreview: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			waiting: false,
			waitingForDelete: false,
			itemCondition: ""
		};
	},

	computed: {
		image() {
			const itemImages = this.$options.filters.itemImages(this.basketItem.variation.data.images, "urlPreview");

			return this.$options.filters.itemImage(itemImages);
		},

		width()
        {
            const itemImages = this.$options.filters.itemImages(this.basketItem.variation.data.images, "urlPreview");
            return this.$options.filters.itemImageWidth(itemImages);
        },

        height()
        {
          const itemImages = this.$options.filters.itemImages(this.basketItem.variation.data.images, "urlPreview");
          return this.$options.filters.itemImageHeight(itemImages);
        },

		altText() {
			const images = this.$options.filters.itemImages(this.basketItem.variation.data.images, "urlPreview");
			const altText = this.$options.filters.itemImageAlternativeText(images);

			if (altText) {
				return altText;
			}

			return this.itemName;
		},

		itemName() {
			return this.$options.filters.itemName(this.basketItem.variation.data);
		},

		isInputLocked() {
			return this.waiting || this.isBasketLoading;
		},

		propertySurchargeSum() {
			let sum = 0;

			for (const property of this.basketItem.basketItemOrderParams) {
				sum += this.$options.filters.propertySurcharge(
					this.basketItem.variation.data.properties,
					property.propertyId
				);
			}

			return sum;
		},

		unitPrice() {
			let setComponentsParamSurcharge = 0;
			if (isDefined(this.basketItem?.setComponents)) {
				setComponentsParamSurcharge = this.basketItem.setComponents
					.map((component) => component.quantity * component.attributeTotalMarkup)
					.reduce((sum, i) => sum + i, 0);
			}
			return this.basketItem.price + setComponentsParamSurcharge;
		},

        basePrice()
        {
            // if the 'AfterBasketItemUpdate' event contains a new base price for the item, return it
            if (!isNullOrUndefined(this.basketItem.updatedBasePrice))
            {
                return this.basketItem.updatedBasePrice;
            }

            if (!isNullOrUndefined(this.basketItem.variation.data.prices.specialOffer))
            {
                return this.basketItem.variation.data.prices.specialOffer.basePrice;
            }

            return this.basketItem.variation.data.prices.default.basePrice;
        },

		...mapState({
			isBasketLoading: (state) => state.basket.isBasketLoading,
			isCheckoutReadonly: (state) => state.checkout.readOnly,
			showNetPrice: (state) => state.basket.showNetPrices
		})
	},

	methods: {
		/**
		 * Delete item from basket
		 */
		deleteItem() {
			if (!this.waiting && !this.waitingForDelete && !this.isBasketLoading) {
				this.waitingForDelete = true;

				this.$store.dispatch("removeBasketItem", this.basketItem.id).then(
					(response) => {
						document.dispatchEvent(
							new CustomEvent("afterBasketItemRemoved", {
								detail: this.basketItem
							})
						);
						this.waitingForDelete = false;
					},
					(error) => {
						this.waitingForDelete = false;
					}
				);
			}
		},

		/**
		 * Update item quantity in basket
		 * @param quantity
		 */
		updateQuantity(quantity) {
			if (this.basketItem.quantity !== quantity) {
				this.waiting = true;

				const origQty = this.basketItem.quantity;

				this.$store
					.dispatch("updateBasketItemQuantity", {
						id: this.basketItem.id,
						variationId: this.basketItem.variation.id,
						quantity: quantity
					})
					.then(
						(response) => {
							document.dispatchEvent(
								new CustomEvent("afterBasketItemQuantityUpdated", { detail: this.basketItem })
							);
							this.waiting = false;
						},
						(error) => {
							this.basketItem.quantity = origQty;

							if (this.isPreview) {
								this.$store.dispatch("addBasketNotification", {
									type: "error",
									message: TranslationService.translate(
										"Ceres::Template." + ExceptionMap.get(error.data.exceptionCode.toString()),
										error.data.placeholder
									)
								});
							} else {
								NotificationService.error(
									TranslationService.translate(
										"Ceres::Template." + ExceptionMap.get(error.data.exceptionCode.toString()),
										error.data.placeholder
									)
								).closeAfter(5000);
							}

							this.waiting = false;
						}
					);
			}
		},

		isPropertyVisible(propertyId) {
			const property = this.basketItem.variation.data.properties.find(
				(property) => property.property.id === parseInt(propertyId)
			);

			if (property) {
				return property.property.isShownAtCheckout;
			}

			return false;
		},

		isDataFieldVisible(value) {
			return this.basketDetailsData.includes(value);
		}
	}
};
</script>
